
import dayjs from 'dayjs';
import { Badge, Flex, Grid, Popover, Tag, Tooltip, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { formatDateForDisplay } from '@shared/utils/dateUtils';
import { buildColumns } from 'Utils/tableUtils';
import { ActionsDropdown } from './ActionsDropdown';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const getColumns = () => {
  const screens = useBreakpoint();
  const compactActionsCol = !screens.xl;

  const baseColumnConfig = {
    width: '10em',
  };

  const columnsConfig = [
    {
      title: 'Nombre convenio',
      dataIndex: 'name',
      width: '22%',
      sorter: (a, b) => a.name?.localeCompare(b.name ?? '', 'es', { sensitivity: 'base' }),
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: 'Aseguradora',
      dataIndex: 'insuranceCarrier',
      width: '12%',
      render: (value) => value.name,
      sorter: (a, b) => a.insuranceCarrier.name?.localeCompare(b.insuranceCarrier.name ?? '', 'es', { sensitivity: 'base' }),
    },
    {
      title: 'Estado',
      dataIndex: 'active',
      width: '10%',
      sorter: (a, b) => a.active - b.active,
      render: (value) => (
        <Badge color={value ? 'green' : 'red'} text={value ? 'Activo' : 'Inactivo'} />
      ),
    },
    {
      title: 'Planes',
      dataIndex: 'plans',
      width: '16%',
      render: (value) => {
        if (value.length === 0) return <Tag>-</Tag>;

        const MAX_VISIBLE_WHEN_COLLAPSED = 2;
        const MIN_ITEMS_TO_COLLAPSE = 4;

        // Si hay 4 o más planes se colapsan y solo se muestran los primeros 2, mostrando un tooltip con el resto
        return (
          <Flex gap='small' wrap='wrap'>
            {value.slice(0, value.length >= MIN_ITEMS_TO_COLLAPSE ? MAX_VISIBLE_WHEN_COLLAPSED : value.length).map((plan) => (
              <Tag key={plan.id} style={{ margin: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{plan.name}</Tag>
            ))}
            {value.length >= MIN_ITEMS_TO_COLLAPSE && (
              <Popover
                content={
                  <Flex gap='small' wrap='wrap'>
                    {value.slice(MAX_VISIBLE_WHEN_COLLAPSED).map((plan) => (
                      <Tag key={plan.id} style={{ margin: 0, width: '100%', flex: 0 }}>
                        <Text ellipsis={{ tooltip: true }}>{plan.name}</Text>
                      </Tag>
                    ))}
                  </Flex>
                }
                styles={{ body: { maxWidth: '30em' } }}
                destroyOnHidden  // Garantiza que los tooltips internos funcionen correctamente
              >
                <Tag style={{ margin: 0 }}>+{value.length - MAX_VISIBLE_WHEN_COLLAPSED}</Tag>
              </Popover>
            )}
          </Flex>
        );
      },
    },
    {
      title: 'Fecha de creación',
      dataIndex: 'createdAt',
      sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
      render: (value) => formatDateForDisplay(value),
    },
    {
      title: 'Vigencia',
      dataIndex: 'startDate',
      width: '12em',
      sorter: (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf(),
      render: (value, record) => (
        `${formatDateForDisplay(value)}${record.endDate ? ` – ${formatDateForDisplay(record.endDate)}` : ''}`
      ),
    },
    {
      title: compactActionsCol ? (
        <Tooltip title='Acciones'>
          <FontAwesomeIcon icon={faEllipsisV} style={{ width: '100%' }} />
        </Tooltip>
      ) : (
        'Acciones'
      ),
      width: '4em',
      render: (_, record) => (
        <ActionsDropdown record={record} compact={compactActionsCol} />
      ),
    },
  ];

  const finalColumnsConfig = columnsConfig.map((column) => ({
    ...column,
    ...(!!column.sorter && { showSorterTooltip: { mouseEnterDelay: 0.5 } }), // Se agrega un delay de 0.5 segundos al mostrar el tooltip
    ...(typeof column.title === 'string' && { title: <span style={{ whiteSpace: 'nowrap' }}>{column.title}</span> }), // Asegura que el título no se rompa en varias líneas
  }));

  return buildColumns(finalColumnsConfig, baseColumnConfig);
};